import { useEffect, useRef, useState } from "react";
import { ChevronDown, User, Check, Loader2 } from "lucide-react";
import { LogoutButton } from "./LogoutButton";
import { useLocation } from "wouter";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import { MorphPopover, MorphPopoverContent, MorphPopoverTrigger } from "./motion/popover-morph";

/**
 * Header account switcher — one selected-account control for the personal
 * account and each company, with deleted companies separate under history.
 * Selection scopes private data; authorization always happens server-side.
 */
export function ContextSwitcher() {
  const { user, setUser, logout } = useAuth();
  const [, navigate] = useLocation();
  const [open, setOpen] = useState(false);
  const [ctx, setCtx] = useState<Awaited<ReturnType<typeof api.getContexts>> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const root = useRef<HTMLDivElement>(null);

  // Preload contexts so the switcher can decide whether to render (hidden for accounts with no company).
  useEffect(() => {
    let alive = true;
    if (user) api.getContexts().then((contexts) => { if (alive) setCtx(contexts); })
      .catch(() => { if (alive) setCtx({ active: null, personal: [], companies: [], historyCompanies: [] }); });
    return () => { alive = false; };
  }, [user]);

  if (!user) return null;
  const active = user.activeContext || null;
  const isCompanyActive = !!active?.startsWith("company:");
  const activeCompany = [...(ctx?.companies ?? []), ...(ctx?.historyCompanies ?? [])].find((c) => c.key === active);
  const isHistory = ctx?.historyCompanies?.some((c) => c.key === active) ?? false;
  const personalName = [user.firstName, user.lastName].filter(Boolean).join(" ") || "Personal account";
  const isAdmin = user.userType === "admin";
  const activeLabel = isCompanyActive ? activeCompany?.label || "Business profile" : personalName;
  const compactActiveLabel = activeLabel.trim().split(/\s+/, 1)[0] || activeLabel;
  const activeType = isCompanyActive ? (isHistory ? "Business history" : "Company") : "Personal account";

  async function pick(key: string | null) {
    if (busy) return;
    setError(null);
    setBusy(key || "personal");
    try {
      const u = await api.switchContext(key);
      if (!u) throw new Error("Please sign in again to switch profiles.");
      setUser(u);
      setOpen(false);
      if (key?.startsWith("company:")) navigate(`/company/${key.slice("company:".length)}/dashboard`);
      else navigate(isAdmin ? "/admin" : "/dashboard");
    }
    catch (failure: unknown) { setError(failure instanceof Error ? failure.message : "Could not switch profiles. Please retry."); } finally { setBusy(null); }
  }

  async function doLogout() {
    await logout();
    setOpen(false);
    navigate("/");
  }
  // Prefer worker personal context, else client, else null
  const personalKey = ctx?.personal?.[0]?.key ?? null;

  return (
    <div ref={root}><MorphPopover open={open} onOpenChange={next => {
      setOpen(next);
      if (!next) root.current?.querySelector<HTMLButtonElement>("button")?.focus({ preventScroll: true });
    }}>
      <MorphPopoverTrigger><button type="button"
        className="h-9 px-1.5 pr-2.5 rounded-lg flex items-center gap-2 max-w-[260px] font-semibold text-sm transition btn-ghost"
        title={activeLabel} aria-label={`Switch account: ${activeLabel}`} aria-expanded={open}>
        {isCompanyActive
          ? <span className="w-7 h-7 rounded-full bg-navy text-white grid place-items-center text-[10px] font-bold shrink-0">{activeCompany?.initials || "B"}</span>
          : <span className="w-7 h-7 rounded-full bg-navy-50 text-navy grid place-items-center text-xs font-bold overflow-hidden shrink-0">{user.profileImageUrl ? <img src={api.apiAssetUrl(user.profileImageUrl) || undefined} alt="" className="w-full h-full object-cover" /> : (user.firstName?.[0] || "") + (user.lastName?.[0] || "")}</span>}
        <span className="truncate">{compactActiveLabel}</span>
        <ChevronDown className="w-3.5 h-3.5 shrink-0 text-mut" />
      </button></MorphPopoverTrigger>

      <MorphPopoverContent align="end" className="w-72 max-w-[calc(100vw-24px)] max-h-[70dvh] overflow-y-auto rounded-2xl border border-line bg-white p-1.5" onOpenAutoFocus={panel => panel.querySelector<HTMLButtonElement>("button:not([disabled])")?.focus()}>
          {!ctx ? (
            <div className="py-4 text-center text-mut"><Loader2 className="w-4 h-4 animate-spin mx-auto" /></div>
          ) : (
            <>
              <div className="flex items-center gap-2 px-2 py-2.5 border-b border-line mb-1.5">
                {isCompanyActive
                  ? <span className="w-8 h-8 rounded-full bg-navy text-white grid place-items-center text-[11px] font-bold shrink-0">{activeCompany?.initials || "B"}</span>
                  : <span className="w-8 h-8 rounded-full bg-navy-50 text-navy grid place-items-center text-xs font-bold overflow-hidden shrink-0">{user.profileImageUrl ? <img src={api.apiAssetUrl(user.profileImageUrl) || undefined} alt="" className="w-full h-full object-cover" /> : (user.firstName?.[0] || "") + (user.lastName?.[0] || "")}</span>}
                <div className="min-w-0"><div className="text-sm font-semibold text-navy truncate">{activeLabel}</div><div className="text-xs text-mut">{activeType}</div></div>
              </div>
              <button disabled={busy !== null} onClick={() => pick(personalKey)} className={`w-full flex items-center gap-2 px-2 py-2 rounded-lg text-sm hover:bg-navy-50 text-left ${!isCompanyActive ? "bg-navy-50" : ""}`}>
                <User className="w-4 h-4 text-navy shrink-0" /><span className="flex-1 min-w-0"><span className="block font-medium text-navy">Personal account</span><span className="block text-[11px] text-mut truncate">{personalName}</span></span>
                {!isCompanyActive && <Check className="w-4 h-4 text-brand-green shrink-0" />}
                {busy === "personal" && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              </button>
              {ctx.companies.map((c) => (
                <button key={c.key} disabled={busy !== null} onClick={() => pick(c.key)} className={`w-full flex items-center gap-2 px-2 py-2 rounded-lg text-sm hover:bg-navy-50 text-left ${active === c.key ? "bg-navy-50" : ""}`}>
                  <span className="w-5 h-5 rounded bg-navy text-white grid place-items-center text-[10px] font-bold shrink-0">{c.initials}</span>
                  <span className="flex-1 min-w-0"><span className="block text-[11px] text-mut">Company</span><span className="block truncate font-medium text-navy">{c.label}</span></span>
                  {active === c.key && <Check className="w-4 h-4 text-brand-green shrink-0" />}
                  {busy === c.key && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                </button>
              ))}
              {!!ctx.historyCompanies?.length && <div className="mt-1.5 border-t border-line px-2 pt-2 text-xs font-semibold text-mut">Business history</div>}
              {(ctx.historyCompanies ?? []).map((c) => (
                <button key={c.key} disabled={busy !== null} onClick={() => pick(c.key)} className={`w-full flex items-center gap-2 px-2 py-2 rounded-lg text-sm hover:bg-navy-50 text-left ${active === c.key ? "bg-navy-50" : ""}`}>
                  <span className="w-5 h-5 rounded bg-navy-50 text-navy grid place-items-center text-[10px] font-bold shrink-0">{c.initials}</span>
                  <span className="flex-1 min-w-0"><span className="block text-[11px] text-mut">Business history</span><span className="block truncate font-medium text-navy">{c.label}</span></span>
                  {active === c.key && <Check className="w-4 h-4 text-brand-green shrink-0" />}
                  {busy === c.key && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                </button>
              ))}
              {error && <p role="alert" className="px-2 py-1 text-xs text-brand-red">{error}</p>}
              <div className="mt-1.5 border-t border-line pt-1.5">
                <LogoutButton onLogout={doLogout} disabled={busy !== null} variant="ghost"
                  className="min-h-11 w-full justify-start rounded-lg px-2 py-2 text-left text-sm font-medium text-brand-red hover:bg-brand-red/5" />
              </div>
            </>
          )}
      </MorphPopoverContent>
    </MorphPopover></div>
  );
}
